// ---------------------------------------------------------------------------
// Gravador de "travadinhas" (quadros longos) — só diagnóstico, só com "?dbg=1".
//
// Mede e atribui causa; NÃO corrige nada e NÃO altera comportamento do app.
//  - Um requestAnimationFrame (no overlay) chama frame(): quadro longo = intervalo entre dois
//    callbacks >= 25 ms (e >= 50 ms em separado).
//  - Pontos já existentes do app chamam markEvent(tipo, valor): cada chamada só escreve 3 números
//    num buffer circular pré-alocado. Sem a flag, markEvent retorna na primeira linha.
//  - Um quadro longo recebe como causas os eventos ocorridos ENTRE o callback anterior e o atual
//    (é nesse intervalo que o trabalho que atrasou o quadro rodou).
//  - Heap (performance.memory, se existir): queda >= 2 MB entre amostras de 250 ms = "GC provável".
//  - Zero alocação por quadro: só typed arrays pré-alocados e números.
// ---------------------------------------------------------------------------

import { DBG_ENABLED } from './dbgFlag';

export const EV = {
  LOTE: 0, LOTE_PAREDE: 1, CARGA_INICIO: 2, CARGA_FIM: 3, MATH_TICK: 4, MATH_CARGA: 5,
  COMMIT_DADOS: 6, ESCALA_DEGRAU: 7, TEXTO: 8, BUF_LINHAS: 9, BUF_VELAS: 10, TRACKERS: 11,
  CROSSHAIR: 12, REACT: 13, EIXOS: 14, CAMERA: 15, BLOCO_OUTRO: 16, GC: 17,
  // Sequência de uma troca de lote (sondas do filamento): disparo -> commit -> upload dos buffers
  LOTE_DISPARO: 18, COMMIT_LOTE: 19, COMMIT_LOTE_PAREDE: 20, UPLOAD_FIL: 21, UPLOAD_CAND: 22,
  // P1 (medição): janela de velas e degrau grande de escala
  DESPEJO: 23, COSTURA: 24, DEGRAU10: 25,
} as const;
export const NEV = 26;
export const EV_NAMES: string[] = [
  'troca de lote', 'troca de lote (parede)', 'carga: início', 'carga: fim', 'processMathData (tick)', 'processMathData (carga)',
  'commit de dados (React)', 'escala: degrau grande', 'texto 3D dos rótulos', 'buffer das linhas', 'buffer das velas', 'trackers',
  'crosshair', 'render React', 'eixos (calculateSmartTicks)', 'câmera/useFrame', 'outro bloco do profiler', 'GC provável',
  'lote: disparo (ChartDataSlicer)', 'lote: commit do React', 'lote da parede: commit', 'upload do filamento (GPU)', 'upload dos candles (GPU)',
  'despejo de velas', 'costura da cauda', 'escala: degrau >= 10%',
];
export const MODE_NAMES = ['parado', 'arrasto manual', 'autoplay 15', 'autoplay 60', 'autoplay 180'];
export const NMODE = 5;

const LONG_MS = 25;
const LONG2_MS = 50;
const BIN_PER_MS = 2;           // histograma de tempo de quadro: bins de 0,5 ms
const NBINS = 500;              // até 250 ms
const GC_DROP_MB = 2;
const GC_BACKFILL_MS = 300;     // a queda de heap é percebida na amostra seguinte: marca os quadros longos recentes

class LongRing {
  readonly cap: number;
  n = 0;
  head = 0;
  t: Float64Array; dur: Float32Array; mode: Uint8Array; pos: Float32Array;
  mask: Uint32Array; topMs: Float32Array; topType: Uint8Array;
  constructor(cap: number) {
    this.cap = cap;
    this.t = new Float64Array(cap); this.dur = new Float32Array(cap); this.mode = new Uint8Array(cap);
    this.pos = new Float32Array(cap); this.mask = new Uint32Array(cap); this.topMs = new Float32Array(cap);
    this.topType = new Uint8Array(cap);
  }
  push(t: number, dur: number, mode: number, pos: number, mask: number, topMs: number, topType: number) {
    const i = this.head;
    this.t[i] = t; this.dur[i] = dur; this.mode[i] = mode; this.pos[i] = pos;
    this.mask[i] = mask; this.topMs[i] = topMs; this.topType[i] = topType;
    this.head = (i + 1) % this.cap; this.n++;
  }
  get size() { return this.n < this.cap ? this.n : this.cap; }
  /** Índice físico do k-ésimo registro, do mais antigo (0) ao mais novo (size-1). */
  at(k: number) { return (this.head - this.size + k + this.cap * 2) % this.cap; }
}

/**
 * Modo de navegação (índice de MODE_NAMES). Estado do app primeiro (gesto de arrasto / retorno ao presente com
 * a velocidade escolhida); se o estado não disser nada mas o histórico anda sozinho, classifica pela velocidade
 * medida (candles/s, suavizada): 15, 60 ou 180 (limites 30 e 104 = médias geométricas). Saltos > 600/s
 * (reancoragem, troca de posição) não contam como autoplay.
 */
export function navMode(drag: boolean, returning: boolean, sens: number, speed: number): number {
  if (drag) return 1;
  if (returning) return sens === 3 ? 4 : sens === 2 ? 3 : 2;
  const a = speed < 0 ? -speed : speed;
  if (a < 3 || a > 600) return 0;
  return a < 30 ? 2 : a < 104 ? 3 : 4;
}

export interface LoafEntry { t: number; dur: number; block: number; url: string; invoker: string; pos: number }

const pct = (a: number, b: number) => (b > 0 ? ((a / b) * 100).toFixed(0) + '%' : '—');
const f = (x: number, d = 1) => (isFinite(x) ? x.toFixed(d) : '—');

export class JankRecorder {
  // ---- eventos
  private evCap: number;
  private evT: Float64Array; private evType: Uint8Array; private evVal: Float32Array;
  private evHead = 0;
  readonly evCount = new Float64Array(NEV);
  private evFirst = new Float64Array(NEV);
  private evLast = new Float64Array(NEV);
  private mathCarga = false;
  // ---- quadros
  readonly ring25: LongRing;
  readonly ring50: LongRing;
  private prevNow = 0;
  t0 = 0;
  fpsEma = 16.7;
  pauses = 0;
  lastMode = 0;
  readonly modeFrames = new Float64Array(NMODE);
  readonly modeTime = new Float64Array(NMODE);
  readonly modeLong25 = new Float64Array(NMODE);
  readonly modeLong50 = new Float64Array(NMODE);
  private modeBins = new Uint32Array(NMODE * NBINS);
  // ---- overhead do instrumento
  ovN = 0; ovSum = 0; ovMax = 0;
  // ---- heap
  heapSupported = false;
  private lastHeap = NaN;
  heapMB = NaN;
  gcCount = 0;
  private reg = new Float64Array(NMODE * 5); // por modo: n, Σx, Σy, Σxx, Σxy (x em minutos, y em MB)
  private regAll = new Float64Array(5);
  private regMinX = new Float64Array(NMODE).fill(Infinity);
  private regMaxX = new Float64Array(NMODE).fill(-Infinity);
  // ---- marcas
  readonly markCap: number;
  markN = 0; private markHead = 0;
  markT: Float64Array; markMode: Uint8Array; markPos: Float32Array; markFps: Float32Array;
  // ---- long animation frames
  loafSupported: boolean | null = null;
  private loafCap = 200;
  private loaf: LoafEntry[] = [];

  constructor(opts: { longCap?: number; long50Cap?: number; markCap?: number; evCap?: number } = {}) {
    this.ring25 = new LongRing(opts.longCap ?? 2000);
    this.ring50 = new LongRing(opts.long50Cap ?? 500);
    this.markCap = opts.markCap ?? 500;
    this.markT = new Float64Array(this.markCap); this.markMode = new Uint8Array(this.markCap);
    this.markPos = new Float32Array(this.markCap); this.markFps = new Float32Array(this.markCap);
    this.evCap = opts.evCap ?? 512;
    this.evT = new Float64Array(this.evCap); this.evType = new Uint8Array(this.evCap); this.evVal = new Float32Array(this.evCap);
  }

  // ------------------------------------------------------------------ eventos
  event(t: number, type: number, value = 0): void {
    const i = this.evHead;
    this.evT[i] = t; this.evType[i] = type; this.evVal[i] = value;
    this.evHead = (i + 1) % this.evCap;
    if (this.evCount[type] === 0) this.evFirst[type] = t;
    this.evCount[type]++; this.evLast[type] = t;
  }
  setMathCtx(carga: boolean) { this.mathCarga = carga; }
  get inCargaMath() { return this.mathCarga; }

  // ------------------------------------------------------------------ quadros
  /** Um chamado por quadro (rAF). `now` = performance.now() no início do callback. */
  frame(now: number, mode: number, pos: number): void {
    const prev = this.prevNow;
    this.prevNow = now;
    if (prev === 0) { this.t0 = now; return; }
    this.lastMode = mode;
    const dt = now - prev;
    // P1b T6(d): o primeiro quadro depois da volta do segundo plano é o congelamento; 2 s fora das estatísticas
    if (this.bgPending) { this.bgPending = false; if (dt > this.bgMaxFreeze) this.bgMaxFreeze = dt; }
    if (now < this.bgUntil) return;
    if (dt > 5000) { this.pauses++; return; } // aba em segundo plano / suspensão: não é travadinha
    this.fpsEma = this.fpsEma * 0.95 + dt * 0.05;
    this.modeFrames[mode]++; this.modeTime[mode] += dt;
    let b = (dt * BIN_PER_MS) | 0; if (b >= NBINS) b = NBINS - 1;
    this.modeBins[mode * NBINS + b]++;
    if (dt < LONG_MS) return;
    // causas: eventos entre o callback anterior e este (o buffer de eventos é ordenado no tempo)
    let mask = 0, topMs = 0, topType = 255;
    let i = this.evHead;
    for (let k = 0; k < this.evCap; k++) {
      i = i === 0 ? this.evCap - 1 : i - 1;
      if (this.evT[i] <= prev) break;
      const ty = this.evType[i];
      mask |= 1 << ty;
      const v = this.evVal[i];
      if (v > topMs) { topMs = v; topType = ty; }
    }
    this.ring25.push(now, dt, mode, pos, mask, topMs, topType);
    this.modeLong25[mode]++;
    if (dt >= LONG2_MS) { this.ring50.push(now, dt, mode, pos, mask, topMs, topType); this.modeLong50[mode]++; }
  }

  overhead(ms: number) { this.ovN++; this.ovSum += ms; if (ms > this.ovMax) this.ovMax = ms; }

  // ------------------------------------------------------------------ P1b T6(d): volta do segundo plano
  /** Até quando (performance.now) os quadros, alarmes e FPS ficam fora das estatísticas. */
  bgUntil = 0; bgN = 0; bgMaxFreeze = 0; private bgPending = false;
  /** A aba voltou do segundo plano (visibilitychange → visible): marca e exclui os próximos 2 s. */
  bgReturn(now: number): void { this.bgN++; this.bgUntil = now + 2000; this.bgPending = true; }
  /** Horário do evento mais antigo ainda no anel (início da janela das taxas ao acaso). */
  oldestEventT(): number { const t = this.evT[this.evHead]; return t > 0 ? t : this.evT[0]; }

  // ------------------------------------------------------------------ heap / GC
  /** Amostra do heap (chamar a cada ~250 ms, não por quadro). */
  heap(now: number, usedBytes: number, mode: number): void {
    this.heapSupported = true;
    const mb = usedBytes / 1048576;
    this.heapMB = mb;
    if (isFinite(this.lastHeap) && this.lastHeap - mb >= GC_DROP_MB) {
      this.gcCount++;
      this.event(now, EV.GC, this.lastHeap - mb);
      this.backfillGc(now);
    }
    this.lastHeap = mb;
    if (this.t0 === 0) return;
    const x = (now - this.t0) / 60000;
    const r = this.reg, o = mode * 5;
    r[o]++; r[o + 1] += x; r[o + 2] += mb; r[o + 3] += x * x; r[o + 4] += x * mb;
    const a = this.regAll; a[0]++; a[1] += x; a[2] += mb; a[3] += x * x; a[4] += x * mb;
    if (x < this.regMinX[mode]) this.regMinX[mode] = x;
    if (x > this.regMaxX[mode]) this.regMaxX[mode] = x;
  }
  private backfillGc(now: number) {
    for (const ring of [this.ring25, this.ring50]) {
      for (let k = ring.size - 1; k >= 0; k--) {
        const idx = ring.at(k);
        if (ring.t[idx] < now - GC_BACKFILL_MS) break;
        ring.mask[idx] |= 1 << EV.GC;
      }
    }
  }
  private slope(s: ArrayLike<number>, o: number): number {
    const n = s[o]; if (n < 4) return NaN;
    const den = n * s[o + 3] - s[o + 1] * s[o + 1];
    if (den <= 1e-9) return NaN;
    return (n * s[o + 4] - s[o + 1] * s[o + 2]) / den; // MB por minuto
  }

  // ------------------------------------------------------------------ marcas
  mark(now: number, mode: number, pos: number): void {
    const i = this.markHead;
    this.markT[i] = now; this.markMode[i] = mode; this.markPos[i] = pos; this.markFps[i] = 1000 / this.fpsEma;
    this.markHead = (i + 1) % this.markCap; this.markN++;
  }

  // ------------------------------------------------------------------ marcas do filamento ("Marcar filamento")
  readonly fmCap = 500;
  fmN = 0; private fmHead = 0;
  fmT = new Float64Array(500); fmMode = new Uint8Array(500); fmPos = new Float32Array(500);
  markFil(now: number, mode: number, pos: number): void {
    const i = this.fmHead;
    this.fmT[i] = now; this.fmMode[i] = mode; this.fmPos[i] = pos;
    this.fmHead = (i + 1) % this.fmCap; this.fmN++;
  }

  /** Máscara de eventos ocorridos depois de `t` (até agora). Sem alocação; usada pelas sondas por quadro. */
  maskSince(t: number): number {
    let mask = 0, i = this.evHead;
    for (let k = 0; k < this.evCap; k++) {
      i = i === 0 ? this.evCap - 1 : i - 1;
      if (this.evT[i] <= t) break;
      mask |= 1 << this.evType[i];
    }
    return mask;
  }
  /** Máscara de eventos com `a < t <= b` (janela dos dois lados de um instante). Sem alocação; só para a resolução tardia da P1. */
  maskBetween(a: number, b: number): number {
    let mask = 0, i = this.evHead;
    for (let k = 0; k < this.evCap; k++) {
      i = i === 0 ? this.evCap - 1 : i - 1;
      const t = this.evT[i];
      if (t <= a) break;
      if (t <= b) mask |= 1 << this.evType[i];
    }
    return mask;
  }
  /** Maior quadro longo (>= 25 ms) com `a <= t <= b`; 0 se não houver. */
  longestBetween(a: number, b: number): number {
    const r = this.ring25; let best = 0;
    for (let k = r.size - 1; k >= 0; k--) {
      const i = r.at(k), t = r.t[i];
      if (t < a) break;
      if (t <= b && r.dur[i] > best) best = r.dur[i];
    }
    return best;
  }
  labelsOf(mask: number): string { return this.labels(mask); }

  // ------------------------------------------------------------------ long animation frames
  addLoaf(e: LoafEntry) { this.loaf.push(e); if (this.loaf.length > this.loafCap) this.loaf.shift(); }

  // ------------------------------------------------------------------ consultas (fora do caminho quente)
  recentLong(now: number, windowMs: number, minDur = LONG_MS): number {
    const ring = minDur >= LONG2_MS ? this.ring50 : this.ring25;
    let c = 0;
    for (let k = ring.size - 1; k >= 0; k--) { const i = ring.at(k); if (ring.t[i] < now - windowMs) break; c++; }
    return c;
  }

  private labels(mask: number): string {
    if (mask === 0) return 'sem causa conhecida';
    const out: string[] = [];
    for (let e = 0; e < NEV; e++) if (mask & (1 << e)) out.push(EV_NAMES[e]);
    return out.join(' + ');
  }
  private pctBin(mode: number, p: number): number {
    const base = mode * NBINS; let total = 0;
    for (let i = 0; i < NBINS; i++) total += this.modeBins[base + i];
    if (total === 0) return NaN;
    let acc = 0; const target = total * p;
    for (let i = 0; i < NBINS; i++) { acc += this.modeBins[base + i]; if (acc >= target) return (i + 0.5) / BIN_PER_MS; }
    return NBINS / BIN_PER_MS;
  }

  /** Seção "TRAVADINHAS" do "Copiar log". */
  report(now: number, t0Abs: number): string[] {
    const L: string[] = [];
    const rel = (t: number) => f((t - t0Abs) / 1000, 1) + 's';
    const sessionMs = this.prevNow > 0 ? this.prevNow - this.t0 : 0;
    const totalFrames = this.modeFrames.reduce((a, b) => a + b, 0);
    const n25 = this.ring25.n, n50 = this.ring50.n;
    L.push('=== TRAVADINHAS ===');
    L.push('LEGENDA: quadro longo = intervalo entre dois quadros >= 25 ms (>= 50 ms contado à parte). Causas = eventos ocorridos naquele intervalo; um quadro pode ter várias.');
    L.push(`sessão medida: ${f(sessionMs / 1000, 0)}s | quadros: ${totalFrames} | quadros longos >=25ms: ${n25} (${f(n25 / Math.max(1e-9, sessionMs / 60000), 1)}/min) | >=50ms: ${n50} | pausas >5s ignoradas: ${this.pauses}`);
    L.push(`instrumento: overhead médio ${f((this.ovSum / Math.max(1, this.ovN)) * 1000, 1)} µs/quadro, máx ${f(this.ovMax, 3)} ms | heap: ${this.heapSupported ? f(this.heapMB, 1) + ' MB (performance.memory é aproximado no Chrome)' : 'performance.memory NÃO disponível'} | GC provável: ${this.gcCount} | Long Animation Frames: ${this.loafSupported === null ? 'não consultado' : this.loafSupported ? 'suportado' : 'NÃO suportado neste navegador'}`);

    // (a) últimos quadros longos
    const N = 30;
    L.push('');
    L.push(`--- (a) últimos ${Math.min(N, this.ring25.size)} quadros longos ---`);
    for (let k = Math.max(0, this.ring25.size - N); k < this.ring25.size; k++) {
      const i = this.ring25.at(k);
      const top = this.ring25.topType[i] !== 255 ? ` | maior: ${EV_NAMES[this.ring25.topType[i]]} ${f(this.ring25.topMs[i], 1)}ms` : '';
      L.push(`t=${rel(this.ring25.t[i])} ${f(this.ring25.dur[i], 0)}ms ${MODE_NAMES[this.ring25.mode[i]]} pos=${f(this.ring25.pos[i], 0)} [${this.labels(this.ring25.mask[i])}]${top}`);
    }

    // (b) histograma de causas
    L.push('');
    for (const [name, ring] of [['>=25ms', this.ring25], ['>=50ms', this.ring50]] as [string, LongRing][]) {
      const sz = ring.size; const cnt = new Float64Array(NEV); let none = 0;
      for (let k = 0; k < sz; k++) { const m = ring.mask[ring.at(k)]; if (m === 0) none++; for (let e = 0; e < NEV; e++) if (m & (1 << e)) cnt[e]++; }
      L.push(`--- (b) causas dos quadros ${name} (${sz} quadros; % dos quadros que têm a causa) ---`);
      const rows: [string, number][] = [];
      for (let e = 0; e < NEV; e++) if (cnt[e] > 0) rows.push([EV_NAMES[e], cnt[e]]);
      rows.push(['sem causa conhecida', none]);
      rows.sort((a, b) => b[1] - a[1]);
      L.push(rows.map(([n, c]) => `${n}: ${pct(c, sz)} (${c})`).join(' | ') || '—');
    }

    // (c) periodicidade
    L.push('');
    L.push('--- (c) periodicidade ---');
    {
      const sz = this.ring25.size; const d: number[] = [];
      for (let k = 1; k < sz; k++) d.push(this.ring25.t[this.ring25.at(k)] - this.ring25.t[this.ring25.at(k - 1)]);
      d.sort((a, b) => a - b);
      const q = (p: number) => (d.length ? d[Math.min(d.length - 1, Math.floor(p * d.length))] : NaN);
      L.push(`intervalo entre quadros longos: mediana ${f(q(0.5) / 1000, 2)}s | p90 ${f(q(0.9) / 1000, 2)}s (${d.length} intervalos)`);
      const mean = (e: number) => (this.evCount[e] >= 2 ? f((this.evLast[e] - this.evFirst[e]) / (this.evCount[e] - 1) / 1000, 2) + 's' : '—');
      L.push(`intervalo médio dos eventos: troca de lote ${mean(EV.LOTE)} | troca de lote (parede) ${mean(EV.LOTE_PAREDE)} | tick de dados (processMathData) ${mean(EV.MATH_TICK)} | commit de dados ${mean(EV.COMMIT_DADOS)} | carga ${mean(EV.CARGA_FIM)} | texto 3D ${mean(EV.TEXTO)} | GC provável ${mean(EV.GC)}`);
    }

    // (d) marcas
    L.push('');
    {
      const sz = Math.min(this.markN, this.markCap);
      let explained = 0; const cnt = new Float64Array(NEV); let none = 0;
      const lines: string[] = [];
      for (let k = 0; k < sz; k++) {
        const i = (this.markHead - sz + k + this.markCap * 2) % this.markCap;
        const tm = this.markT[i];
        let best = -1, bd = 401;
        for (let j = this.ring25.size - 1; j >= 0; j--) {
          const idx = this.ring25.at(j); const dtt = Math.abs(this.ring25.t[idx] - tm);
          if (this.ring25.t[idx] < tm - 401) break;
          if (dtt < bd) { bd = dtt; best = idx; }
        }
        let desc = 'nenhum quadro longo em ±400ms';
        if (best >= 0) {
          explained++; const m = this.ring25.mask[best];
          if (m === 0) none++; for (let e = 0; e < NEV; e++) if (m & (1 << e)) cnt[e]++;
          desc = `quadro longo ${f(this.ring25.dur[best], 0)}ms a ${f(this.ring25.t[best] - tm, 0)}ms da marca [${this.labels(m)}]`;
        }
        lines.push(`t=${rel(tm)} ${MODE_NAMES[this.markMode[i]]} pos=${f(this.markPos[i], 0)} fps≈${f(this.markFps[i], 0)} → ${desc}`);
      }
      L.push(`--- (d) marcas: ${sz} | explicadas por quadro longo em ±400ms: ${pct(explained, sz)} ---`);
      const rows: [string, number][] = [];
      for (let e = 0; e < NEV; e++) if (cnt[e] > 0) rows.push([EV_NAMES[e], cnt[e]]);
      if (none) rows.push(['sem causa conhecida', none]);
      rows.sort((a, b) => b[1] - a[1]);
      L.push(`o que mais explica as marcas: ${rows.map(([n, c]) => `${n} ${pct(c, explained)}`).join(' | ') || '—'}`);
      for (const ln of lines.slice(-40)) L.push(ln);
    }

    // (e) por modo
    L.push('');
    L.push('--- (e) por modo ---');
    L.push('modo | tempo | quadros | longos>=25 | >=50 | longos/min | FPS p50 | FPS p5 | heap MB/min');
    for (let m = 0; m < NMODE; m++) {
      if (this.modeFrames[m] === 0) { L.push(`${MODE_NAMES[m]} | sem dados`); continue; }
      const p50 = this.pctBin(m, 0.5), p95 = this.pctBin(m, 0.95);
      const span = this.regMaxX[m] - this.regMinX[m];
      const sl = span * 60 >= 10 ? this.slope(this.reg, m * 5) : NaN;
      L.push(`${MODE_NAMES[m]} | ${f(this.modeTime[m] / 1000, 0)}s | ${this.modeFrames[m]} | ${this.modeLong25[m]} | ${this.modeLong50[m]} | ${f(this.modeLong25[m] / Math.max(1e-9, this.modeTime[m] / 60000), 1)} | ${f(1000 / p50, 0)} | ${f(1000 / p95, 0)} | ${f(sl, 2)}`);
    }
    L.push(`heap, tendência da sessão inteira: ${f(this.slope(this.regAll, 0), 2)} MB/min (serra do GC torna a tendência ruidosa em sessões curtas)`);

    // Long Animation Frames
    L.push('');
    L.push(`--- Long Animation Frames (últimos ${Math.min(30, this.loaf.length)} de ${this.loaf.length}) ---`);
    if (this.loafSupported === false) L.push('não suportado neste navegador');
    for (const e of this.loaf.slice(-30)) L.push(`t=${rel(e.t)} ${f(e.dur, 0)}ms bloqueante ${f(e.block, 0)}ms | ${e.invoker || '—'} @ ${e.url || '—'}:${e.pos}`);
    return L;
  }
}

// ---------------------------------------------------------------------------
// Singleton + API usada pelo app. Sem a flag: nada é criado e markEvent não faz nada.
// ---------------------------------------------------------------------------
export const jank: JankRecorder | null = DBG_ENABLED ? new JankRecorder() : null;

export function markEvent(type: number, value = 0): void {
  if (!DBG_ENABLED) return;
  jank!.event(performance.now(), type, value);
}

/** Blocos do profiler existente (appProfiler.record): só os >= 4 ms viram evento. */
export function jankBlock(name: string, ms: number): void {
  if (!DBG_ENABLED) return;
  let t: number = EV.BLOCO_OUTRO;
  if (name.includes('FastLine')) t = EV.BUF_LINHAS;
  else if (name.includes('InstancedCandles')) t = EV.BUF_VELAS;
  else if (name.includes('CurrentValueTrackers')) t = EV.TRACKERS;
  else if (name.includes('CrosshairRenderer')) t = EV.CROSSHAIR;
  else if (name.startsWith('React:')) t = EV.REACT;
  else if (name.startsWith('Axes:')) t = EV.EIXOS;
  else if (name.startsWith('useFrame:SceneContent')) t = EV.CAMERA;
  else if (name.startsWith('Math:')) return; // já marcado por dbgMathCall (tick/carga)
  jank!.event(performance.now(), t, ms);
}
